import React from "react";

/**
 * Video format and creative type — the picker, shared by both creative paths.
 *
 * AUTONOMOUS-WIZARD-THREE, 2026-09-09. The Founder asked for this in the
 * Autonomous wizard repeatedly and it kept not landing. The reason was
 * structural rather than anyone forgetting: the option lists lived inside
 * `services/custom_creative_session/config.py` and were surfaced only on a
 * Custom session's response, so **the Autonomous path had nothing to render**.
 * The lists moved to `services/creative_formats.py`, both session responses now
 * carry them under the same keys, and this component draws them for either.
 *
 * WHY THE CHOICE IS ON THE SESSION, NOT PER SCRIPT. A Custom session records one
 * format and one type for the whole run, and that is what names the delivered
 * file. The Autonomous path only ever asked per script, at step 6, which is
 * both later and finer-grained than the question actually is.
 *
 * FOUNDER RULING 2026-09-09 — ASKED ONCE, FULL STOP. "The video format and
 * video-length choice should only exist on the very first screen, not repeated
 * on every later step... Pick the format and length once, up front, and every
 * later step should just use that stored choice rather than asking again." The
 * wizard was asking four times: format and type here, the creative type AGAIN
 * per script on step 6, the platform and the length on step 4, and a channel
 * and a type a fourth time in the assembly panel. Step 6's dropdown is gone and
 * step 4's two are gone; this is the only place any of it is asked.
 *
 * THE LENGTH JOINED IT, and it had nowhere to live before: verified live, **0 of
 * 120 sessions carried a length field**. Step 4 asked for it, used it to pick
 * the script's structure, and stored the answer on the SCRIPT only — so the
 * session had no length, and every later step that wanted one had to guess or
 * ask again.
 *
 * NEITHER FIELD IS DEFAULTED. `TRELLO_CREATIVE_TYPE = "VE"` used to stand in
 * for a choice nobody made, and the Founder found the consequence in a
 * delivered filename: an AI Video session named `…-VE-…`. An unmade choice
 * shows as unmade.
 */
export default function FormatTypePicker({
  videoFormat, creativeType, videoLength,
  formatOptions = [], typeOptions = [], lengthOptions = [],
  onFormatChange, onTypeChange, onLengthChange,
  disabled = false, testidPrefix = "format-type",
}) {
  // A screen that cannot offer the options must not pretend to ask. Empty
  // dropdowns look like a choice and are not one.
  if (!formatOptions.length && !typeOptions.length && !lengthOptions.length) {
    return (
      <p className="text-slate-500 text-xs" data-testid={`${testidPrefix}-unavailable`}>
        Video format, creative type and length could not be loaded for this
        session, so they cannot be chosen here. This is a failed read, not a
        session with no options — nothing has been lost, and the choice is still
        unmade.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid={testidPrefix}>
      <label className="block">
        <span className="block text-xs text-slate-400 mb-1">Creative type</span>
        <select
          value={creativeType || ""}
          onChange={(e) => onTypeChange(e.target.value)}
          disabled={disabled}
          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
          data-testid={`${testidPrefix}-type`}
        >
          <option value="">Choose…</option>
          {typeOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="block text-xs text-slate-400 mb-1">Video format</span>
        <select
          value={videoFormat || ""}
          onChange={(e) => onFormatChange(e.target.value)}
          disabled={disabled}
          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
          data-testid={`${testidPrefix}-format`}
        >
          <option value="">Choose…</option>
          {/* A stored value the current list no longer offers is kept as an
              option rather than silently reset to "Choose…", which would read as
              "you never picked anything". */}
          {videoFormat && !formatOptions.includes(videoFormat) && (
            <option value={videoFormat}>{videoFormat}</option>
          )}
          {formatOptions.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </label>

      {/* THE LENGTH. It drives the script's STRUCTURE — 15-30 is four sections,
          45-60 is six — so it is a real choice and not a label, and it is asked
          here with the other two rather than in the middle of step 4. */}
      <label className="block">
        <span className="block text-xs text-slate-400 mb-1">Video length</span>
        <select
          value={videoLength || ""}
          onChange={(e) => onLengthChange && onLengthChange(e.target.value)}
          disabled={disabled || !onLengthChange}
          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
          data-testid={`${testidPrefix}-length`}
        >
          <option value="">Choose…</option>
          {videoLength && !lengthOptions.some((o) => o.value === videoLength) && (
            <option value={videoLength}>{videoLength}</option>
          )}
          {lengthOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </label>
    </div>
  );
}
